import {
  FlowModuleItemType,
  ModuleInputKeyEnum,
} from "@packages/flow-core/module";
import { NodeProps } from "reactflow";
import NodeCard from "../NodeCard";
import Divider from "../Divider";
import styles from "./index.less";
import RenderOutput from "../render/RenderOutput";
import { RenderInputProps } from "../render/RenderInput/types";
import RenderInput from "../render/RenderInput";
import { useFlow } from "../FlowEdit/useFlow";
import { memo, useRef, useState } from "react";
import {
  Button,
  Form,
  FormInstance,
  Input,
  Modal,
  Row,
  Table,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import useToken from "antd/es/theme/useToken";
import { FlowNodeOutputItemType } from "@packages/flow-core/module/node/types";
import RenderToolInput from "../render/RenderToolInput";
import { ReactComponent as EditIcon } from "../../../assets/edit.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/delete.svg";
import classNames from "classnames";

type FormData = {
  key: string;
  description: string;
};
const ExtractKeyModal = ({
  onClose,
  onConfirm,
  visible,
  data,
}: {
  onClose: () => void;
  onConfirm: (data: FormData) => void;
  visible: boolean;
  data?: FormData;
}) => {
  const ref = useRef<FormInstance<FormData>>(null);

  async function handleConfirm() {
    const values = await ref.current?.validateFields();
    onConfirm(values!);
  }

  return (
    <Modal
      open={visible}
      title="提取字段配置"
      okText="确认"
      cancelText="取消"
      onOk={handleConfirm}
      afterClose={() => ref.current?.resetFields()}
      onCancel={onClose}
      destroyOnClose
    >
      <Form ref={ref} initialValues={data}>
        <Form.Item label="字段KEY" name="key" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label="字段描述" name="description">
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const ExtractKeys = ({ item, moduleId }: RenderInputProps) => {
  const [, token] = useToken();
  const [open, setOpen] = useState(false);
  const {
    updateNodeValue,
    addModuleOutput,
    deleteModuleOutput,
    updateModuleOutput,
  } = useFlow();
  const dataRef = useRef<FormData>();
  return (
    <>
      <Row justify="end" style={{ marginBottom: token.marginSM }}></Row>
      <Table
        className={classNames("pro-table", styles.table)}
        locale={{ emptyText: "暂无数据" }}
        pagination={false}
        dataSource={item.value as { key: string; description: string }[]}
        size="small"
        bordered
        columns={[
          { title: "字段 KEY", dataIndex: "key" },
          { title: "字段描述", dataIndex: "description" },
          {
            title: "操作",
            align: "center",
            width: 80,
            render(_v, data) {
              return (
                <Row justify="center">
                  <EditIcon
                    className={styles["text-icon"]}
                    onClick={() => {
                      setOpen(true);
                      dataRef.current = data;
                    }}
                  />
                  <DeleteIcon
                    style={{ marginLeft: 8 }}
                    className={classNames(styles["text-icon"], styles.danger)}
                    onClick={() => {
                      const newValue = (item.value as FormData[]).filter(
                        (item) => item.key !== data.key
                      );
                      updateNodeValue(moduleId, item.key, newValue);
                      deleteModuleOutput(moduleId, data.key);
                    }}
                  />
                </Row>
              );
            },
          },
        ]}
      />
      <Button
        icon={<PlusOutlined />}
        onClick={() => {
          dataRef.current = undefined;
          setOpen(true);
        }}
        block
        className={styles["add-row-btn"]}
      >
        新增字段
      </Button>
      <ExtractKeyModal
        visible={open}
        onClose={() => setOpen(false)}
        onConfirm={(data) => {
          if (
            (dataRef.current && dataRef.current.key !== data.key) ||
            !dataRef.current
          ) {
            const existed = ((item.value as Array<FormData>) || []).find(
              (it) => it.key === data.key
            );
            if (existed) {
              message.warning("该key值已经存在");
              return;
            }
          }
          const outputNode: FlowNodeOutputItemType = {
            key: data.key,
            type: "source",
            valueType: "string",
            targets: [],
            label: `提取结果-${data.key}`,
            description: data.description,
            edit: true,
          };
          setOpen(false);
          if (dataRef.current) {
            const index = (item.value as Array<FormData>).findIndex(
              (item) => item.key === dataRef.current!.key
            );
            const newValue = [...((item.value || []) as Array<FormData>)];
            newValue.splice(index, 1, data);
            updateNodeValue(moduleId, item.key, [...newValue]);
            updateModuleOutput(moduleId, dataRef.current.key, outputNode);
          } else {
            const newValue = ((item.value as Array<FormData>) || []).concat(
              data
            );
            updateNodeValue(moduleId, item.key, newValue);
            addModuleOutput(moduleId, outputNode);
          }
        }}
        data={dataRef.current}
      />
    </>
  );
};

export default memo(function NodeContentExtract({
  selected,
  data,
}: NodeProps<FlowModuleItemType>) {
  const { inputs, outputs, moduleId } = data;
  const { splitToolInputs } = useFlow();
  const { toolInputs, commonInputs } = splitToolInputs(inputs, moduleId);

  return (
    <NodeCard {...data} selected={selected} minWidth={400}>
      {toolInputs.length > 0 && (
        <>
          <Divider text="工具输入" />
          <div className={styles.content}>
            <RenderToolInput moduleId={moduleId} inputs={toolInputs} />
          </div>
        </>
      )}
      <Divider text="输入" />
      <div className={styles.content}>
        <RenderInput
          moduleId={moduleId}
          flowInputList={commonInputs}
          CustomComponent={{
            [ModuleInputKeyEnum.contentExtractKeys]: ExtractKeys,
          }}
        />
      </div>
      <Divider text="输出" />
      <div className={styles.content}>
        <RenderOutput flowOutputList={outputs} moduleId={moduleId} />
      </div>
    </NodeCard>
  );
});
